import { type SectionEditorContext } from '@/models/analytics/features/notes';
import { EditorType } from '@heidi/editor/core';
import { createContext, useContext } from 'react';

export const EditorTypeContext = createContext<EditorType | undefined>(undefined);

/**
 * Whether this editor surface mirrors the note copy setting by suppressing
 * bold/italic/underline. Separate from `EditorType.NOTE`: Haiku also uses that
 * enum but is not the NoteTab surface and must keep its formatting affordances.
 */
export const EmphasisDisabledContext = createContext(false);

/** The NoteTab-only emphasis suppression state for this editor subtree. */
export function useEmphasisDisabledContext(): boolean {
  return useContext(EmphasisDisabledContext);
}

function assertNever(value: never): never {
  throw new Error(`Unhandled EditorType: ${String(value)}`);
}

/**
 * Maps an `EditorType` to the section analytics `editor_context`. Exhaustive by
 * design — `assertNever` makes a new `EditorType` a compile error here rather
 * than letting it silently fall through to `note`. `ASK_AI` / `CONTEXT` don't
 * enable sections, so they're unreachable today and map to `note`.
 */
export function toSectionEditorContext(editorType?: EditorType): SectionEditorContext {
  switch (editorType) {
    case EditorType.TEMPLATE:
      return 'template';
    case EditorType.DOCUMENT:
      return 'document';
    case EditorType.NOTE:
    case EditorType.ASK_AI:
    case EditorType.CONTEXT:
    case undefined:
      return 'note';
    default:
      return assertNever(editorType);
  }
}

/** The section analytics `editor_context` for the surrounding editor. */
export function useSectionEditorContext(): SectionEditorContext {
  return toSectionEditorContext(useContext(EditorTypeContext));
}
